"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, BookOpen, Download, FileText } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const featuredGuides = [{
  id: "g1",
  category: "Strategic RevOps",
  title: "The B2B Revenue Architecture Playbook",
  description: "A structured blueprint for aligning pipeline telemetry, executive compensation models, and retention operations.",
  format: "Executive Brief",
  pages: "44 pages",
  fileSize: "4.2 MB PDF",
  edition: "2025 Edition",
  accentTone: "border-l-primary"
}, {
  id: "g2",
  category: "Enterprise AI",
  title: "Governing Generative Systems in Regulated Markets",
  description: "Risk frameworks, audit trails, and data sovereignty standards for deploying large models within financial institutions.",
  format: "Research Guide",
  pages: "58 pages",
  fileSize: "5.8 MB PDF",
  edition: "Q2 Release",
  accentTone: "border-l-primary"
}, {
  id: "g3",
  category: "Market Expansion",
  title: "Capital Allocation in Volatile Tech Cycles",
  description: "Empirical modeling on balance sheet resilience, organic acquisition pipelines, and cross-border unit economics.",
  format: "Strategic Guide",
  pages: "36 pages",
  fileSize: "3.6 MB PDF",
  edition: "Annual Monograph",
  accentTone: "border-l-primary"
}];
export default function Guides() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="featured-ebooks-guides" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
        {/* Masthead Header with Broadsheet Rules */}
        <div className="mb-12 sm:mb-16">
          <div className="flex items-center justify-between gap-4 mb-3">
            <span className="text-xs font-semibold tracking-wider text-muted-foreground uppercase font-sans">
              Curated Publications
            </span>
            <span className="text-xs font-mono text-muted-foreground hidden sm:inline-block">
              Institutional Library
            </span>
          </div>

          <div className="w-full h-px bg-foreground mb-1" />
          <div className="w-full h-[0.5px] bg-border mb-6" />

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="max-w-2xl">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold tracking-tight leading-tight text-foreground">
                Executive guides and strategic playbooks
              </h2>
              <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
                Download peer-reviewed methodologies, framework briefs, and
                operational blueprints prepared by our senior research staff.
              </p>
            </div>

            <div className="shrink-0">
              <Button variant="outline" className="rounded-sm border-border text-foreground hover:bg-muted font-sans" asChild>
                <Link to="/EbooksGuidesPage" className="inline-flex items-center gap-2">
                  <span>Browse full catalogue</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* 3-Column Publication Showcase */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
          {featuredGuides.map((guide, i) => <motion.article key={guide.id} data-index={i} variants={itemVariants} whileHover={shouldReduceMotion ? {} : {
          y: -4
        }} transition={{
          duration: 0.25,
          ease: "easeOut"
        }} className="group flex flex-col justify-between bg-card text-card-foreground rounded-md border border-border p-6 shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-250">
              {/* Publication Physical Spine Mockup */}
              <div className="relative aspect-[3/2] w-full rounded-sm bg-muted/60 border border-border p-5 flex flex-col justify-between overflow-hidden mb-6 select-none">
                {/* Book Spine Crease Treatment */}
                <div className="absolute inset-y-0 left-0 w-4 pointer-events-none bg-gradient-to-r from-foreground/15 via-background/40 to-transparent" aria-hidden="true" />

                {/* Top Spine Metadata */}
                <div className="flex items-center justify-between gap-2 pl-3">
                  <Badge variant="outline" className="rounded-sm bg-background border-border text-[11px] font-mono font-medium text-muted-foreground">
                    {guide.format}
                  </Badge>
                  <span className="text-[11px] font-mono text-muted-foreground">
                    {guide.edition}
                  </span>
                </div>

                {/* Center Book Art Preview */}
                <div className="pl-3 pr-2 py-2">
                  <div className="flex items-center gap-2 text-muted-foreground mb-2">
                    <BookOpen className="size-4 text-muted-foreground" />
                    <span className="text-xs uppercase font-sans font-semibold tracking-wider text-muted-foreground">
                      {guide.category}
                    </span>
                  </div>
                  <h3 className="font-serif text-lg font-semibold leading-snug line-clamp-2 text-foreground group-hover:text-primary transition-colors">
                    {guide.title}
                  </h3>
                </div>

                {/* Bottom Spine Specifications */}
                <div className="flex items-center justify-between text-xs font-mono text-muted-foreground pt-3 border-t border-border/70 pl-3">
                  <span className="flex items-center gap-1.5">
                    <FileText className="size-3.5" />
                    {guide.pages}
                  </span>
                  <span>{guide.fileSize}</span>
                </div>
              </div>

              {/* Card Meta & Content */}
              <div className="flex-1 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2.5">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {guide.category}
                    </span>
                  </div>

                  <h4 className="text-xl font-serif font-semibold text-foreground leading-snug mb-3 group-hover:text-primary transition-colors">
                    {guide.title}
                  </h4>

                  <p className="text-sm text-muted-foreground font-sans leading-relaxed mb-6">
                    {guide.description}
                  </p>
                </div>

                {/* Download CTA Action Row */}
                <div className="pt-4 border-t border-border flex items-center justify-between">
                  <span className="text-xs font-mono text-muted-foreground">
                    Free with verification
                  </span>
                  <Button variant="secondary" size="sm" className="rounded-sm bg-secondary text-secondary-foreground hover:bg-muted font-sans text-xs font-medium" asChild>
                    <Link to="/EbooksGuidesPage" className="inline-flex items-center gap-1.5">
                      <Download className="size-3.5" />
                      <span>Access Guide</span>
                    </Link>
                  </Button>
                </div>
              </div>
            </motion.article>)}
        </motion.div>

        {/* Footnote Editorial Guarantee */}
        <div className="mt-14 pt-6 border-t border-border/80 flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-xs text-muted-foreground font-sans">
          <p>
            All MinfoMedia publications adhere to strict editorial peer-review
            and zero-sponsor bias methodologies.
          </p>
          <div className="flex items-center gap-4">
            <Link to="/PrivacyDataProtectionPage" className="text-muted-foreground hover:text-foreground underline underline-offset-4 transition-colors">
              Privacy & Data Policy
            </Link>
            <span aria-hidden="true" className="text-border">
              |
            </span>
            <Link to="/ResearchReportsPage" className="text-muted-foreground hover:text-foreground underline underline-offset-4 transition-colors">
              Research Reports
            </Link>
          </div>
        </div>
      </div>
    </section>;
}